import { useState } from "react"

import { formatCurrency } from "~/utils/finance/format"

import type {
  PricingKindBundle,
  PricingSavedWarranty,
  PricingWarrantyOption,
  WarrantyStepBundle,
} from "./pricing-kind-bundle"
import {
  PricingLoading,
  PricingStepCloseFooter,
  PricingStepDrawer,
  PricingStepFooter,
  ToggleCard,
  ToggleCardGrid,
} from "./pricing-step-chrome"
import { usePricingSaveGuard } from "./use-pricing-save-guard"

const SAVED_PROTECTION = `Saved Protection`

const WarrantyCard = ({
  option,
  selected,
  disabled,
  hint,
  onToggle,
}: {
  option: PricingWarrantyOption
  selected: boolean
  disabled: boolean
  hint?: string
  onToggle: () => void
}): JSX.Element => (
  <ToggleCard
    selected={selected}
    disabled={disabled}
    minHeightClass="min-h-32"
    onToggle={onToggle}
    title={
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="font-display text-[length:var(--step-0)] font-semibold text-foreground">
          {option.name}
        </span>
        {hint ? (
          <span className="text-[length:var(--step--2)] text-bp-gold">
            {hint}
          </span>
        ) : null}
      </span>
    }
  >
    <span className="self-end text-[length:var(--step-0)] font-semibold text-bp-accent tabular-nums">
      {formatCurrency(option.cost)}
    </span>
  </ToggleCard>
)

const WarrantyCatalog = ({
  bundle,
  proposalId,
  options,
  saved,
  unavailableReason,
  canEdit,
  onClose,
}: {
  bundle: WarrantyStepBundle
  proposalId: string
  options: readonly PricingWarrantyOption[]
  saved: PricingSavedWarranty | null
  unavailableReason: string | null
  canEdit: boolean
  onClose: () => void
}): JSX.Element => {
  const saveWarranty = bundle.useSave(proposalId)
  const guard = usePricingSaveGuard({ onClose, savedFeedback: true })
  // Draft lives only here; unmounting (any close path) discards it.
  // `undefined` means no draft: render the saved selection.
  const [draft, setDraft] = useState<number | null | undefined>(undefined)
  const savedWarrantyId = saved?.powurOsId ?? null
  const selectedId = draft === undefined ? savedWarrantyId : draft
  const saving = saveWarranty.isLoading
  const locked = !canEdit || saving || guard.justSaved

  // A saved selection the fresh catalog no longer offers still renders, so
  // the seller can remove it directly; it can never be re-drafted.
  const staleSaved =
    saved != null && !options.some((option) => option.id === saved.powurOsId)
      ? saved
      : null

  // A proposal keeps zero or one Extended Warranty: choosing an option
  // replaces the previous choice, choosing it again removes it.
  const toggle = (id: number): void => {
    saveWarranty.reset()
    setDraft(selectedId === id ? null : id)
  }

  const deselectStale = (): void => {
    saveWarranty.reset()
    if (selectedId != null && selectedId === staleSaved?.powurOsId) {
      setDraft(null)
    }
  }

  const submit = (): void => {
    if (locked || !guard.start()) return
    saveWarranty.run(selectedId, {
      onSettled: guard.release,
      onSuccess: () => {
        setDraft(undefined)
        guard.succeed()
      },
    })
  }

  return (
    <>
      {unavailableReason ? (
        <p
          role="status"
          className="mb-4 text-[length:var(--step--1)] text-muted"
        >
          {unavailableReason}
        </p>
      ) : null}
      {options.length === 0 && staleSaved == null ? (
        unavailableReason ? null : (
          <p role="status" className="text-[length:var(--step--1)] text-muted">
            {bundle.copy.empty}
          </p>
        )
      ) : (
        <ToggleCardGrid testId="warranty-cards-container">
          {staleSaved ? (
            <WarrantyCard
              option={{
                id: staleSaved.powurOsId,
                name: staleSaved.name ?? SAVED_PROTECTION,
                cost: staleSaved.cost ?? 0,
              }}
              selected={selectedId === staleSaved.powurOsId}
              disabled={locked}
              hint="No longer offered"
              onToggle={deselectStale}
            />
          ) : null}
          {options.map((option) => (
            <WarrantyCard
              key={option.id}
              option={option}
              selected={selectedId === option.id}
              disabled={locked}
              onToggle={() => toggle(option.id)}
            />
          ))}
        </ToggleCardGrid>
      )}

      <PricingStepFooter
        onClose={onClose}
        error={
          saveWarranty.isError
            ? {
                message: `Unable to save Protection. Your selection is still here.`,
                onRetry: submit,
              }
            : null
        }
        savedMessage={guard.justSaved ? `Protection saved` : null}
        save={
          canEdit
            ? {
                label: `Save Protection`,
                saving,
                disabled: locked,
                onSave: submit,
              }
            : null
        }
      />
    </>
  )
}

/**
 * The provider catalog failing to load never blocks removal: the saved
 * snapshot is proposal-owned, and a remove is a null save that needs no
 * catalog.
 */
const CatalogUnavailable = ({
  bundle,
  proposalId,
  saved,
  onRetry,
  canEdit,
  onClose,
}: {
  bundle: WarrantyStepBundle
  proposalId: string
  saved: PricingSavedWarranty | null
  onRetry: () => void
  canEdit: boolean
  onClose: () => void
}): JSX.Element => {
  const removeWarranty = bundle.useSave(proposalId)
  const guard = usePricingSaveGuard({ onClose, savedFeedback: false })

  const remove = (): void => {
    if (removeWarranty.isLoading || !guard.start()) return
    removeWarranty.run(null, {
      onSettled: guard.release,
      onSuccess: guard.succeed,
    })
  }

  return (
    <div className="flex flex-col gap-2">
      <p role="alert" className="text-[length:var(--step--2)] text-danger">
        {bundle.copy.loadFailed}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="self-start rounded-md border border-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground"
      >
        Retry
      </button>
      {saved && canEdit ? (
        <div className="mt-4 flex flex-col gap-2 rounded-lg border border-bp-border bg-bp-elevated p-4">
          <p className="text-[length:var(--step--1)] text-foreground">
            <span className="font-semibold">
              {saved.name ?? SAVED_PROTECTION}
            </span>
            {` is saved on this proposal. It can be removed without the catalog.`}
          </p>
          {removeWarranty.isError ? (
            <p role="alert" className="text-[length:var(--step--2)] text-danger">
              Unable to remove Protection. Try again.
            </p>
          ) : null}
          <button
            type="button"
            disabled={removeWarranty.isLoading}
            onClick={remove}
            className="self-start rounded-md border border-bp-danger/50 px-3 py-1.5 text-[length:var(--step--2)] font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {removeWarranty.isLoading ? `Removing` : `Remove Protection`}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** The read failed outright: the kind supplies the saved snapshot, if it can. */
const CatalogReadFailed = ({
  bundle,
  proposalId,
  ...props
}: {
  bundle: WarrantyStepBundle
  proposalId: string
  onRetry: () => void
  canEdit: boolean
  onClose: () => void
}): JSX.Element => (
  <CatalogUnavailable
    bundle={bundle}
    proposalId={proposalId}
    saved={bundle.useSavedWhenCatalogFails(proposalId)}
    {...props}
  />
)

/**
 * Protection Plans: zero or one Extended Warranty, chosen as a draft and
 * written on Save. No pricing prerequisite: the selection is a pricing input,
 * chosen before the first pricing calculation.
 */
export const PricingWarrantyStep = ({
  kind,
  proposalId,
  onClose,
}: {
  kind: PricingKindBundle
  proposalId: string
  onClose: () => void
}): JSX.Element => {
  const bundle = kind.warranty
  const warrantiesQuery = bundle.useCatalog(proposalId)
  const financingLock = kind.useFinancingLock(proposalId)
  const canEdit = !financingLock.locked && !financingLock.pending
  const catalog = warrantiesQuery.isSuccess ? warrantiesQuery.data : undefined
  const retry = (): void => {
    void warrantiesQuery.refetch()
  }

  return (
    <PricingStepDrawer title={bundle.copy.title} onClose={onClose}>
      {financingLock.notice}
      {warrantiesQuery.isLoading ? (
        <PricingLoading>{bundle.copy.loading}</PricingLoading>
      ) : null}
      {warrantiesQuery.isError ? (
        <CatalogReadFailed
          bundle={bundle}
          proposalId={proposalId}
          onRetry={retry}
          canEdit={canEdit}
          onClose={onClose}
        />
      ) : null}
      {catalog?.catalogUnavailable ? (
        <CatalogUnavailable
          bundle={bundle}
          proposalId={proposalId}
          saved={catalog.selected}
          onRetry={retry}
          canEdit={canEdit}
          onClose={onClose}
        />
      ) : null}

      {catalog != null && catalog.catalogUnavailable !== true ? (
        <WarrantyCatalog
          bundle={bundle}
          proposalId={proposalId}
          options={catalog.options}
          saved={catalog.selected}
          unavailableReason={catalog.unavailableReason ?? null}
          canEdit={canEdit}
          onClose={onClose}
        />
      ) : (
        <PricingStepCloseFooter onClose={onClose} />
      )}
    </PricingStepDrawer>
  )
}
